import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, TrendingUp, Award, MapPin, Repeat, Trophy, Tag, Plus, X, ListChecks } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ConfrontoStagioniPanel from "../components/ConfrontoStagioniPanel";

const BADGE_COLORS = {
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  purple: "border-purple-500/40 bg-purple-500/10 text-purple-200",
  sky: "border-sky-500/40 bg-sky-500/10 text-sky-200",
  emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
  slate: "border-slate-700 bg-slate-800/60 text-slate-300",
};

const OP_META = {
  acquisto: { emoji: "💰", label: "Acquisto", color: "emerald" },
  cessione: { emoji: "✈️", label: "Cessione", color: "rose" },
  rinnova: { emoji: "🔁", label: "Rinnovo", color: "sky" },
  svincolo: { emoji: "🕊️", label: "Svincolo", color: "amber" },
  rate_pagamento: { emoji: "📅", label: "Rata pagata", color: "slate" },
  trasferimento_manuale: { emoji: "🔀", label: "Trasferimento", color: "purple" },
};

export default function StoricoGiocatore() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [magnati, setMagnati] = useState(null);
  const [newAlias, setNewAlias] = useState("");
  const [busyAlias, setBusyAlias] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get(`/magnati/rooster/${playerId}`),
          api.get(`/magnati/lega`).catch(() => ({ data: null })),
        ]);
        setPlayer(r.data);
        setMagnati(m.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [playerId]);

  const isAdmin = useMemo(() => {
    if (!user || !magnati) return false;
    return magnati.admin_id === user.user_id || (magnati.co_admins || []).includes(user.user_id);
  }, [user, magnati]);

  const chartData = useMemo(() => {
    if (!player) return [];
    return (player.career || [])
      .sort((a, b) => (a.season_num || 0) - (b.season_num || 0))
      .map(c => ({
        season: c.season,
        valore: Number(c.final_value) || 0,
        costo: Number(c.acquisition_cost) || 0,
        stipendio: Number(c.salary) || 0,
      }));
  }, [player]);

  const transfers = useMemo(() => {
    if (!player) return [];
    const career = (player.career || []).sort((a, b) => (a.season_num || 0) - (b.season_num || 0));
    const t = [];
    for (let i = 1; i < career.length; i++) {
      if (career[i - 1].club_id !== career[i].club_id) {
        t.push({
          from_season: career[i - 1].season, to_season: career[i].season,
          from_club: career[i - 1].club_current_name || "?", to_club: career[i].club_current_name || "?",
          from_club_id: career[i - 1].club_id, to_club_id: career[i].club_id,
          value: career[i].acquisition_cost,
        });
      }
    }
    return t;
  }, [player]);

  async function addAlias() {
    const a = newAlias.trim();
    if (!a) return;
    setBusyAlias(true);
    try {
      const r = await api.post(`/magnati/rooster/${playerId}/aliases`, { alias: a });
      setPlayer(p => ({ ...p, aliases: r.data.aliases }));
      setNewAlias("");
      toast.success(`Alias '${a}' aggiunto`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAlias(false); }
  }

  async function removeAlias(a) {
    if (a.toLowerCase() === (player.canonical_name || "").toLowerCase()) {
      toast.error("Il nome principale non può essere rimosso");
      return;
    }
    if (!window.confirm(`Rimuovere l'alias '${a}'? Il collegamento con eventuali giocatori con quel nome andrà perso.`)) return;
    setBusyAlias(true);
    try {
      await api.delete(`/magnati/rooster/${playerId}/aliases`, { data: { alias: a } });
      setPlayer(p => ({ ...p, aliases: (p.aliases || []).filter(x => x !== a) }));
      toast.success(`Alias '${a}' rimosso`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAlias(false); }
  }

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500">Caricamento…</div>;
  if (!player) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">Giocatore non trovato</div>;

  const records = player.records || {};
  const badges = records.badges || [];
  const cl = player.current_league;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <header className="border-b border-amber-500/20 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => {
              // React Router 6: location.key === "default" per il primo entry (tab nuova, refresh, link diretto)
              // In quel caso NON usare navigate(-1) perché uscirebbe dall'app.
              // Usiamo replace:true per NON preservare il player nella history — così il back del browser
              // dallo storico non ritorna alla pagina giocatore (fine del loop).
              if (location.key && location.key !== "default") {
                navigate(-1);
              } else {
                navigate("/storico-magnati?tab=players", { replace: true });
              }
            }} data-testid="player-back-btn"
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-500/70">Carriera Giocatore</div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
                {player.canonical_name}
              </h1>
              <div className="text-xs text-slate-500 mt-0.5">
                {player.current_ruolo} · Serie A: <span className="uppercase font-mono text-slate-300">{player.current_squadra_a}</span>
                {records.total_seasons ? <span className="ml-3 text-slate-400"><b className="text-slate-200">{records.total_seasons}</b> stagioni · <b className="text-slate-200">{records.unique_clubs}</b> club</span> : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* RECORDS / BADGES */}
        {badges.length > 0 && (
          <Card className="bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-950 border-amber-500/30" data-testid="records-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="w-4 h-4 text-amber-400"/> Record & Achievement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {badges.map((b, i) => (
                  <div key={i} data-testid={`record-badge-${i}`}
                    className={`px-3 py-2 rounded-xl border ${BADGE_COLORS[b.color] || BADGE_COLORS.slate} flex items-start gap-2`}>
                    <div className="text-xl leading-none pt-0.5">{b.emoji}</div>
                    <div className="min-w-0">
                      <div className="text-xs font-black uppercase tracking-wider truncate">{b.title}</div>
                      <div className="text-[10px] opacity-80 leading-snug">{b.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              {(records.highest_price || records.top_value) && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-400">
                  {records.highest_price && (
                    <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                      💸 <b className="text-slate-200">Costo max</b>: {records.highest_price.cost} cr in {records.highest_price.season} ({records.highest_price.club_name || "?"})
                    </div>
                  )}
                  {records.top_value && (
                    <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                      💎 <b className="text-slate-200">Valore top</b>: {records.top_value.value} in {records.top_value.season} ({records.top_value.club_name || "?"})
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Awards Row */}
        {(player.personal_awards || []).length > 0 && (
          <Card className="bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-slate-900 border-amber-500/40">
            <CardContent className="p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4"/> Premi Individuali ({player.personal_awards.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {player.personal_awards.map((aw, i) => (
                  <div key={i} className="px-3 py-1.5 rounded-full bg-slate-950/60 border border-amber-500/50 text-sm">
                    <span className="text-amber-300 font-semibold">{aw.award}</span>
                    <span className="text-slate-500 ml-2 font-mono text-xs">{aw.season}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alias Editor */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="alias-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Tag className="w-4 h-4 text-sky-400"/> Alias (nomi alternativi)
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 ml-2">match automatico con la rosa</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {(player.aliases || []).map((a, i) => {
                const isCanonical = a.toLowerCase() === (player.canonical_name || "").toLowerCase();
                return (
                  <span key={i} data-testid={`alias-tag-${i}`}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs border ${
                      isCanonical ? "bg-amber-500/20 border-amber-500/40 text-amber-200" : "bg-slate-800 border-slate-700 text-slate-300"
                    }`}>
                    {a} {isCanonical && <span className="text-[9px] font-mono opacity-70">CANONICO</span>}
                    {isAdmin && !isCanonical && (
                      <button onClick={() => removeAlias(a)} disabled={busyAlias}
                        data-testid={`remove-alias-btn-${i}`}
                        className="ml-1 rounded-full p-0.5 hover:bg-rose-500/30 text-slate-500 hover:text-rose-300 disabled:opacity-40">
                        <X className="w-3 h-3"/>
                      </button>
                    )}
                  </span>
                );
              })}
              {(player.aliases || []).length === 0 && <span className="text-xs text-slate-500 italic">Nessun alias registrato.</span>}
            </div>
            {isAdmin && (
              <div className="flex gap-2 pt-2 border-t border-slate-800">
                <input
                  type="text" value={newAlias} onChange={e => setNewAlias(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addAlias(); }}
                  placeholder="es. Doig B., Vlahović D."
                  data-testid="new-alias-input"
                  className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600"
                />
                <button onClick={addAlias} disabled={busyAlias || !newAlias.trim()}
                  data-testid="add-alias-btn"
                  className="px-3 py-1.5 rounded-md bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/40 text-xs font-bold uppercase tracking-wider disabled:opacity-50 inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5"/> Aggiungi
                </button>
              </div>
            )}
            {!isAdmin && (
              <div className="text-[10px] text-slate-500 italic">Solo admin/co-admin possono modificare gli alias.</div>
            )}
          </CardContent>
        </Card>

        {/* Value chart */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400"/>Andamento Valore</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="season" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }}
                  labelStyle={{ color: "#f59e0b" }} />
                <Legend />
                <Line type="monotone" dataKey="valore" stroke="#f59e0b" strokeWidth={3} name="Valore" dot={{ r: 5 }} />
                <Line type="monotone" dataKey="costo" stroke="#8b5cf6" strokeWidth={2} name="Costo acquisto" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="stipendio" stroke="#22d3ee" strokeWidth={2} name="Stipendio" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Confronto Stagioni — AI insights su carriera Serie A (iter 55) */}
        <ConfrontoStagioniPanel career={player.career || []} />

        {/* Career table */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-amber-400"/>Carriera Stagione per Stagione</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(player.career || []).sort((a, b) => (a.season_num || 0) - (b.season_num || 0)).map((c, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                  <div className="text-xs font-mono text-amber-400 w-14">{c.season}</div>
                  <Link to={`/storico-magnati/club/${c.club_id}`}
                    className="flex-1 font-semibold text-slate-200 hover:text-amber-400">
                    {c.club_current_name || "?"}
                  </Link>
                  <div className="text-xs text-slate-500 uppercase font-mono">{c.ruolo}</div>
                  <div className="text-xs text-slate-500 uppercase font-mono">{c.squadra_a}</div>
                  <div className="text-right w-20 text-slate-400 font-mono text-sm">costo <span className="text-slate-200">{c.acquisition_cost ?? "—"}</span></div>
                  <div className="text-right w-20 text-amber-300 font-mono text-sm">val <span className="font-bold">{c.final_value ?? "—"}</span></div>
                  {c.svincolato && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">SVINC</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current League Operations */}
        {cl && (
          <Card className="bg-slate-900/60 border-slate-800" data-testid="current-league-ops-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ListChecks className="w-4 h-4 text-emerald-400"/> Operazioni nella lega attuale ({cl.league_name})
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 ml-2">{cl.operations?.length || 0} eventi</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                  <div className="text-[10px] text-slate-500 uppercase font-mono">Squadra Attuale</div>
                  <div className="text-slate-100 font-bold">{cl.team_name || "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                  <div className="text-[10px] text-slate-500 uppercase font-mono">Prezzo Pagato</div>
                  <div className="text-amber-300 font-bold font-mono">{cl.prezzo_pagato != null ? fmtMoney(cl.prezzo_pagato) + " cr" : "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                  <div className="text-[10px] text-slate-500 uppercase font-mono">Stipendio</div>
                  <div className="text-sky-300 font-bold font-mono">{cl.stipendio != null ? fmtMoney(cl.stipendio) + " cr" : "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-950/40 border border-slate-800">
                  <div className="text-[10px] text-slate-500 uppercase font-mono">Quotazione</div>
                  <div className="text-emerald-300 font-bold font-mono">{cl.quotazione_attuale ?? "—"}</div>
                </div>
              </div>
              {cl.operations?.length ? (
                <div className="max-h-80 overflow-y-auto pr-1 space-y-1.5" data-testid="ops-timeline">
                  {cl.operations.map((op, i) => {
                    const m = OP_META[op.type] || { emoji: "📝", label: op.type, color: "slate" };
                    const dateStr = op.created_at ? new Date(op.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "?";
                    return (
                      <div key={op.op_id || i} data-testid={`op-entry-${i}`}
                        className="flex items-start gap-3 px-3 py-2 rounded-lg bg-slate-950/40 border border-slate-800 hover:border-slate-700 transition-colors">
                        <div className="text-lg leading-none pt-0.5">{m.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${BADGE_COLORS[m.color] || BADGE_COLORS.slate}`}>{m.label}</span>
                            {op.amount != null && <span className="text-amber-300 font-mono text-xs">{fmtMoney(op.amount)} cr</span>}
                            <span className="text-[10px] text-slate-500 font-mono ml-auto">{dateStr}</span>
                          </div>
                          {op.notes && <div className="text-xs text-slate-300 mt-0.5">{op.notes}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-slate-500 italic text-center py-2">Nessuna operazione registrata in questa lega.</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Transfers timeline */}
        {transfers.length > 0 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-amber-400"/>Trasferimenti storici ({transfers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {transfers.map((t, i) => (
                  <div key={i} className="flex items-center gap-4 text-sm">
                    <div className="text-xs font-mono text-amber-400 w-24">{t.from_season} → {t.to_season}</div>
                    <Link to={`/storico-magnati/club/${t.from_club_id}`} className="text-slate-400 hover:text-amber-400">{t.from_club}</Link>
                    <span className="text-slate-600">→</span>
                    <Link to={`/storico-magnati/club/${t.to_club_id}`} className="text-slate-200 hover:text-amber-400 font-medium">{t.to_club}</Link>
                    <div className="ml-auto text-xs text-slate-500 font-mono">
                      val: <span className="text-amber-300">{t.value ?? "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
